import { makeStyles, mergeClasses, tokens, Text, Avatar, Badge } from "@fluentui/react-components";
import type { TicketDetailResponse, Comment } from "../api/tickets";
import { useTeamsTheme } from "../hooks/useTeamsTheme";

const useStyles = makeStyles({
  meta: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  metaItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  thread: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  message: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-start",
  },

  // ===== Light theme bubble (default Teams light theme) =====
  bubbleLight: {
    flex: 1,
    minWidth: 0,
    backgroundColor: "#ffffff",
    color: "#201f1e",
    border: `1px solid #edebe9`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
  },
  bubbleLightInternal: {
    backgroundColor: "#fff8c5",
    color: "#3d2e00",
    border: `1px solid #e6c200`,
  },
  bubbleLightHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
    flexWrap: "wrap",
    color: "#605e5c",
  },
  bubbleLightSender: { color: "#201f1e" },
  bubbleLightTime: { marginLeft: "auto", color: "#605e5c" },
  textContentLight: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "#201f1e",
  },
  htmlContentLight: {
    wordBreak: "break-word",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: "#201f1e",
    "& img": { maxWidth: "100%", height: "auto" },
    "& a": { color: "#0067b8" },
    "& blockquote": {
      borderLeft: `3px solid #edebe9`,
      margin: `${tokens.spacingVerticalS} 0`,
      paddingLeft: tokens.spacingHorizontalM,
      color: "#605e5c",
    },
    "& pre": {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      backgroundColor: "#f3f2f1",
      color: "#201f1e",
      padding: tokens.spacingHorizontalS,
      borderRadius: tokens.borderRadiusSmall,
      overflowX: "auto",
    },
    "& table": { maxWidth: "100%", overflow: "auto", display: "block", color: "#201f1e" },
  },

  // ===== Dark theme bubble (Teams dark) =====
  // Force-override inline email colors (which assume white bg) with high-contrast
  // light text. Keep links + images untouched.
  bubbleDark: {
    flex: 1,
    minWidth: 0,
    backgroundColor: "#2b2b2b",
    color: "#f3f2f1",
    border: `1px solid #4a4a4a`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
  },
  bubbleDarkInternal: {
    backgroundColor: "#3a3326",
    color: "#fff4c8",
    border: `1px solid #6b5a1a`,
  },
  bubbleDarkHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
    flexWrap: "wrap",
    color: "#c8c6c4",
  },
  bubbleDarkSender: { color: "#f3f2f1" },
  bubbleDarkTime: { marginLeft: "auto", color: "#c8c6c4" },
  textContentDark: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "#f3f2f1",
  },
  htmlContentDark: {
    wordBreak: "break-word",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: "#f3f2f1",
    // Aggressively override inline colors from email HTML so it stays readable
    // on a dark background. Images and links keep their own colors.
    "& *:not(a):not(img)": {
      color: "#f3f2f1 !important",
      backgroundColor: "transparent !important",
      borderTopColor: "#4a4a4a !important",
      borderRightColor: "#4a4a4a !important",
      borderBottomColor: "#4a4a4a !important",
      borderLeftColor: "#4a4a4a !important",
    },
    "& img": { maxWidth: "100%", height: "auto" },
    "& a, & a *": { color: "#4cc2ff !important" },
    "& blockquote": {
      borderLeft: `3px solid #4a4a4a !important`,
      margin: `${tokens.spacingVerticalS} 0`,
      paddingLeft: tokens.spacingHorizontalM,
      color: "#c8c6c4 !important",
    },
    "& pre, & code": {
      backgroundColor: "#1f1f1f !important",
      color: "#f3f2f1 !important",
    },
    "& pre": {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      padding: tokens.spacingHorizontalS,
      borderRadius: tokens.borderRadiusSmall,
      overflowX: "auto",
    },
    "& table": { maxWidth: "100%", overflow: "auto", display: "block" },
  },
});

function commentSender(comment: Comment, ticket: TicketDetailResponse): string {
  if (comment.authorName) return comment.authorName;
  if (comment.authorEmail) return comment.authorEmail;
  if (!comment.agentId) return ticket.fromName ?? ticket.fromEmail ?? "Contact";
  return "Agent";
}

interface Props {
  ticket: TicketDetailResponse;
  comments: Comment[];
}

export default function TicketDetail({ ticket, comments }: Props) {
  const styles = useStyles();
  const teamsTheme = useTeamsTheme();
  const isDark = teamsTheme === "dark" || teamsTheme === "contrast";

  const cls = {
    bubble: isDark ? styles.bubbleDark : styles.bubbleLight,
    bubbleInternal: isDark ? styles.bubbleDarkInternal : styles.bubbleLightInternal,
    bubbleHeader: isDark ? styles.bubbleDarkHeader : styles.bubbleLightHeader,
    bubbleSender: isDark ? styles.bubbleDarkSender : styles.bubbleLightSender,
    bubbleTime: isDark ? styles.bubbleDarkTime : styles.bubbleLightTime,
    textContent: isDark ? styles.textContentDark : styles.textContentLight,
    htmlContent: isDark ? styles.htmlContentDark : styles.htmlContentLight,
  };

  const contactName = ticket.fromName ?? ticket.fromEmail ?? "Contact";
  const orderedComments = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <div>
      <div className={styles.meta}>
        <div className={styles.metaItem}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Contact</Text>
          <Text size={300}>{ticket.fromName ?? ticket.fromEmail ?? "—"}</Text>
        </div>
        <div className={styles.metaItem}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Assigned to</Text>
          <Text size={300}>{ticket.assignedTo?.name ?? "Unassigned"}</Text>
        </div>
        <div className={styles.metaItem}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Created</Text>
          <Text size={300}>{new Date(ticket.createdAt).toLocaleString()}</Text>
        </div>
        <div className={styles.metaItem}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Updated</Text>
          <Text size={300}>{new Date(ticket.updatedAt).toLocaleString()}</Text>
        </div>
      </div>

      <div className={styles.thread}>
        {ticket.body && (
          <div className={styles.message}>
            <Avatar name={contactName} size={32} />
            <div className={cls.bubble}>
              <div className={cls.bubbleHeader}>
                <Text size={200} weight="semibold" className={cls.bubbleSender}>{contactName}</Text>
                <Text size={100} className={cls.bubbleTime}>
                  {new Date(ticket.createdAt).toLocaleString()}
                </Text>
              </div>
              {ticket.body.contentType?.includes("html") ? (
                <div
                  className={cls.htmlContent}
                  dangerouslySetInnerHTML={{ __html: ticket.body.content }}
                />
              ) : (
                <Text size={300} className={cls.textContent}>
                  {ticket.body.content}
                </Text>
              )}
            </div>
          </div>
        )}

        {orderedComments.map((comment) => {
          const sender = commentSender(comment, ticket);
          return (
            <div key={comment.id} className={styles.message}>
              <Avatar name={sender} size={32} />
              <div
                className={mergeClasses(
                  cls.bubble,
                  comment.isInternal && cls.bubbleInternal
                )}
              >
                <div className={cls.bubbleHeader}>
                  <Text size={200} weight="semibold" className={cls.bubbleSender}>{sender}</Text>
                  {comment.isInternal && (
                    <Badge size="small" appearance="outline" color="warning">Note</Badge>
                  )}
                  {comment.isSentViaEmail && (
                    <Badge size="small" appearance="outline" color="informative">Email</Badge>
                  )}
                  <Text size={100} className={cls.bubbleTime}>
                    {new Date(comment.createdAt).toLocaleString()}
                  </Text>
                </div>
                {comment.contentType === "html" ? (
                  <div
                    className={cls.htmlContent}
                    dangerouslySetInnerHTML={{ __html: comment.content }}
                  />
                ) : (
                  <Text size={300} className={cls.textContent}>
                    {comment.content}
                  </Text>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
